import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { account, cancelled, passkeysSupported, serverFeatures, SPAM_FOLDER } from "../account.ts";
import type { SignedIn } from "../account.ts";
import { ApiError } from "../store.ts";
import { APP, IS_PANTRY } from "../brand.ts";

/** Head and foot of every page without sign-in (as in the sister apps, docs/14-anmeldung.md). */
export function AuthPage({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <h1>
        <img src={`${APP.icons}/apple-touch-icon.png`} alt="" />
        {APP.short}
      </h1>
      <p className="muted tagline">{APP.tagline}</p>
      {children}
      <footer className="muted">
        {APP.short}
        {APP.test && " · Testbetrieb"}
        <LegalLinks />
      </footer>
    </div>
  );
}

/** The tutorials of the app (F26, C3): the same address in both apps, the server picks the pages by host; a window of its own. */
export function TutorialsLink() {
  return (
    <a href="/tutorials" target="_blank" rel="noopener" data-testid="tutorials-link">
      Tutorials ↗
    </a>
  );
}

/** Tutorials, imprint and privacy policy: plain pages of the server, outside the app (docs/14-anmeldung.md). */
export function LegalLinks() {
  return (
    <p className="legal muted">
      <TutorialsLink /> · <a href="/impressum">Impressum</a> · <a href="/datenschutz">Datenschutz</a>
    </p>
  );
}

/** The usual eye for showing a password; crossed out while it is shown (a click hides it again). */
function Eye({ crossed }: { crossed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {crossed && <path d="M3 3l18 18" />}
    </svg>
  );
}

export const messageOf = (err: unknown) =>
  err instanceof ApiError ? err.message : err instanceof TypeError || (err instanceof DOMException && err.name === "AbortError") ? "Keine Verbindung zum Server. Anmelden geht nur mit Netz." : err instanceof Error ? err.message : String(err);

/**
 * User name and password; or a passkey of this device without typing anything. `context`: why the
 * sign-in comes here, above the form (the page to confirm an address).
 */
export function Login({ onSignedIn, context }: { onSignedIn: (s: SignedIn) => void; context?: string }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"form" | "passkey" | null>(null);
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);
  const [passkeysOn, setPasskeysOn] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotOn, setForgotOn] = useState(false);
  const [moved, setMoved] = useState<{ to: string | null; from: string | null }>({ to: null, from: null });

  useEffect(() => {
    serverFeatures().then(
      (f) => [setSignupOpen(f.open), setPasskeysOn(f.passkeys), setForgotOn(f.forgot), setMoved({ to: f.movedTo, from: f.movedFrom })],
      () => setSignupOpen(false),
    );
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) return setError("Bitte gib Benutzername und Passwort ein.");
    setBusy("form");
    try {
      onSignedIn(await account.login(username.trim(), password));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  async function passkey() {
    setError(null);
    setBusy("passkey");
    try {
      onSignedIn(await account.loginWithPasskey());
    } catch (err) {
      if (!cancelled(err)) setError(messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  if (forgot) return <Forgot who={username} on={forgotOn} onBack={() => setForgot(false)} />;
  return (
    <AuthPage>
      <div className="card">
        <h2>Anmelden</h2>
        {moved.to && !context && (
          <p className="notice" data-testid="login-moved">
            {APP.short} ist umgezogen: <a href={moved.to}>{moved.to.replace(/^https?:\/\//, "")}</a>. Melde dich hier an wie gewohnt; danach bist du dort angemeldet. Auf dem Home-Bildschirm bitte neu hinzufügen.
          </p>
        )}
        {context && (
          <p className="notice" data-testid="login-context">
            {context}
          </p>
        )}
        <form noValidate onSubmit={submit}>
          <label>
            Benutzername
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username webauthn" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="username" />
          </label>
          <label>
            Passwort
            <span className="reveal">
              <input type={shown ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="password" />
              <button type="button" className="link" onClick={() => setShown(!shown)} aria-pressed={shown} aria-label={shown ? "Passwort verbergen" : "Passwort anzeigen"} title={shown ? "Passwort verbergen" : "Passwort anzeigen"} data-testid="show-password">
                <Eye crossed={shown} />
              </button>
            </span>
          </label>
          {error && <p className="error-box">{error}</p>}
          <button className="primary" disabled={busy !== null} data-testid="login">
            {busy === "form" ? "Anmelden …" : "Anmelden"}
          </button>
        </form>
        <p className="forgot-link">
          <button type="button" className="link" onClick={() => setForgot(true)} data-testid="forgot">
            Passwort vergessen?
          </button>
        </p>
        {moved.from && (
          <div className="or" data-testid="login-old-address">
            <p className="muted">Dein Passwort ist noch unter der alten Adresse gespeichert? Dort anmelden, du kommst angemeldet hierher zurück.</p>
            <a className="button wide center" href={`${moved.from}/#/mitnehmen`} style={{ display: "block" }} data-testid="login-take-along">
              Unter der alten Adresse anmelden
            </a>
          </div>
        )}
        {passkeysOn && passkeysSupported() && (
          <button className="wide" disabled={busy !== null} onClick={passkey} data-testid="passkey-login">
            {busy === "passkey" ? "Mit Passkey anmelden …" : "Mit Passkey anmelden"}
          </button>
        )}
        {signupOpen && (
          <div className="or">
            <p className="muted">Noch kein Konto?</p>
            <a className="button wide center" href="#/registrieren" style={{ display: "block" }}>
              {IS_PANTRY ? "Neuen Haushalt anlegen" : "Neuen Betrieb oder Keller anlegen"}
            </a>
            <p className="muted">{IS_PANTRY ? "Wer zu einem Haushalt gehört" : "Wer zu einem Betrieb oder Keller gehört"}, bekommt vom Inhaber eine Einladung.</p>
          </div>
        )}
      </div>
    </AuthPage>
  );
}

/**
 * „Passwort vergessen?“ (25.10.2026): user name or e-mail address, then always the same sentence,
 * whether the account exists or not. The link comes by mail and holds an hour; the old password
 * stays until it is used. Off (`on` false: no mail, or FORGOT_BY_MAIL on the server until addresses
 * are confirmed), only the way through the owner.
 */
function Forgot({ who: start, on, onBack }: { who: string; on: boolean; onBack: () => void }) {
  const [who, setWho] = useState(start);
  const [state, setState] = useState<{ sent?: boolean; error?: string; busy?: boolean }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!who.trim()) return setState({ error: "Bitte gib deinen Benutzernamen oder deine E-Mail-Adresse ein." });
    setState({ busy: true });
    try {
      await account.forgot(who.trim());
      setState({ sent: true });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <AuthPage>
      <div className="card" data-testid="forgot-card">
        <h2>Passwort vergessen</h2>
        {!on ? (
          <p data-testid="forgot-off">{APP.short} verschickt hier keine E-Mails. Bitte den Inhaber {IS_PANTRY ? "deines Haushalts oder Betriebs" : "deines Betriebs oder Kellers"} um einen Link für ein neues Passwort.</p>
        ) : state.sent ? (
          <>
            <p className="notice" data-testid="forgot-sent">
              Wenn es das Konto gibt und es eine E-Mail-Adresse hat, ist ein Link unterwegs. Er gilt eine Stunde. Bis dahin bleibt das bisherige Passwort gültig.
            </p>
            <p className="hint">{SPAM_FOLDER}</p>
          </>
        ) : (
          <form noValidate onSubmit={submit}>
            <p>Gib deinen Benutzernamen oder deine E-Mail-Adresse ein. Wir schicken dir einen Link, mit dem du ein neues Passwort festlegst.</p>
            <label>
              Benutzername oder E-Mail-Adresse
              <input value={who} onChange={(e) => [setWho(e.target.value), setState({})]} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="forgot-who" />
            </label>
            {state.error && <p className="error-box">{state.error}</p>}
            <button className="primary" disabled={state.busy} data-testid="forgot-send">
              {state.busy ? "Link schicken …" : "Link schicken"}
            </button>
          </form>
        )}
        {on && <p className="hint">Ohne E-Mail-Adresse im Konto hilft der Inhaber {IS_PANTRY ? "deines Haushalts oder Betriebs" : "deines Betriebs oder Kellers"}: Er erzeugt unter „Konto“ einen Link für ein neues Passwort.</p>}
        <button className="link" onClick={onBack} data-testid="forgot-back">
          Zurück zur Anmeldung
        </button>
      </div>
    </AuthPage>
  );
}
